/**
 * Iter 19.44 — Strukturierter KV-Editor (Phase A).
 *
 * Tab-basierter Editor für die strukturierten KV-Felder. Wird als eigene
 * Seite unter /admin/kvs/:id/struktur eingeblendet, NEBEN dem bestehenden
 * Modal-basierten Editor (der die Freitext-Felder pflegt).
 *
 * Tabs:
 *  1. Stammdaten        — name, kuerzel, beschreibung, cap_months,
 *                         gueltig_von/bis, llm_extra_context
 *  2. Faktoren          — anrechnungs_faktoren (regex + faktor + priorität)
 *  3. Ausbildungs-Boni  — ausbildungsregeln (label + months + synonyme + bypass)
 *  4. Sonderregeln      — sonderregeln (DSL-Trigger + Effekt + Begründung)
 *  5. LLM-Markdown      — Vorschau des autogen Markdown (read-only)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, ArrowLeft, FloppyDisk, CheckCircle, Warning, Eye } from "@phosphor-icons/react";

import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";

const TABS = [
  { id: "stammdaten",    label: "Stammdaten",      testId: "kv-edit-tab-stammdaten" },
  { id: "faktoren",      label: "Faktoren",        testId: "kv-edit-tab-faktoren" },
  { id: "ausbildung",    label: "Ausbildungs-Boni", testId: "kv-edit-tab-ausbildung" },
  { id: "sonderregeln",  label: "Sonderregeln",    testId: "kv-edit-tab-sonderregeln" },
  { id: "capbypass",     label: "Sonderregeln", testId: "kv-edit-tab-capbypass" },
  { id: "markdown",      label: "LLM-Vorschau",    testId: "kv-edit-tab-markdown" },
];

const EMPTY_FAKTOR = {
  trigger_pattern: "",
  faktor: 1.0,
  begruendung_template: "",
  prioritaet: 10,
  aktiv: true,
};

const EMPTY_AUSBILDUNG = {
  label: "",
  synonyms: [],
  months: 0,
  default_anrechenbar: true,
  bypass_cap: false,
};

const EMPTY_SONDERREGEL = {
  name: "",
  trigger_condition: {},
  effekt: {},
  begruendung: "",
  aktiv: true,
};

// Iter 19.66.1 + Iter 20.12 — KV-spezifische „Anrechnungs-Sonderregeln".
// Gruppierung nach Semantik: „Selber-Arbeitgeber" (3 Bypass-Varianten) und
// „Zeitfenster" (Relevanzfenster). Weitere Gruppen können additiv ergänzt
// werden (z. B. Konzernanrechnung, Branchen-Ausschluss, …).
const CAP_BYPASS_TYPES = [
  {
    typ: "selber_arbeitgeber_lueckenregel",
    group: "selber_ag",
    title: "Selber AG · Lückenregel",
    description:
      "Cap-Bypass beim selben Arbeitgeber gilt nur, wenn die zeitliche Lücke zur aktuellen Anstellung ≤ X Monate beträgt.",
    defaults: { max_lueckendauer_monate: 24, faktor: 1.0, max_jahre: 0, zeitfenster_jahre: 0 },
    fields: ["max_lueckendauer_monate"],
  },
  {
    typ: "selber_arbeitgeber_vorarbeiterzeit",
    group: "selber_ag",
    title: "Selber AG · Vorarbeiterzeit",
    description:
      "Vorarbeiterzeiten beim selben Arbeitgeber zählen mit reduziertem Faktor (z. B. 50 %), bis zu einer Obergrenze in Jahren.",
    defaults: { max_lueckendauer_monate: 0, faktor: 0.5, max_jahre: 5, zeitfenster_jahre: 0 },
    fields: ["faktor", "max_jahre"],
  },
  {
    typ: "selber_arbeitgeber_gleiche_gruppe",
    group: "selber_ag",
    title: "Selber AG · Gleiche/Höhere Gruppe",
    description:
      "Cap-Bypass nur, wenn die Vordienst-Position die gleiche oder eine höhere Verwendungsgruppe hatte.",
    defaults: { max_lueckendauer_monate: 0, faktor: 1.0, max_jahre: 0, zeitfenster_jahre: 0 },
    fields: [],
  },
  {
    typ: "zeitfenster_relevanz",
    group: "zeitfenster",
    title: "Zeitfenster · Nur letzte N Jahre",
    description:
      "Nur Vordienstzeiten der letzten X Jahre vor dem Stichtag werden angerechnet. Ältere Zeiten werden ausgeschlossen; teilweise ins Fenster reichende Zeiträume werden am Fenster-Anfang abgeschnitten.",
    defaults: { max_lueckendauer_monate: 0, faktor: 1.0, max_jahre: 0, zeitfenster_jahre: 7 },
    fields: ["zeitfenster_jahre"],
  },
  // Iter 20.36.f2 — WT-Kanzlei-Kombiregel (Steuerberater-/WT-KV).
  // Iter 20.36.f5 — Feldnamen an neue Zwei-Töpfe-Semantik angepasst
  // (siehe Backend `_apply_wt_kanzlei_faktor_reduktion` + Math-Engine).
  {
    typ: "wt_kanzlei_kombiregel",
    group: "wt",
    title: "WT-Kanzlei · Kombiregel",
    description:
      "Zwei-Töpfe-Anrechnung. WT-Kanzlei-Zeilen zählen voll bis max_jahre_wt (Global-Cap). Non-WT-Zeilen halbiert (faktor_non_wt) bis max_jahre_non_wt effektiv.",
    defaults: {
      max_lueckendauer_monate: 0,
      faktor: 1.0,
      max_jahre: 0,
      zeitfenster_jahre: 0,
      max_jahre_wt: 10,
      max_jahre_non_wt: 5,
      faktor_non_wt: 0.5,
    },
    fields: ["max_jahre_wt", "max_jahre_non_wt", "faktor_non_wt"],
  },
  // Iter 20.36.f3 — Altersfaktor-Regel (deterministisch, mit Math-Engine).
  // Halbiert (Faktor typisch 0.5) den Anrechnungsfaktor für anrechenbare
  // Vordienst-Positionen, deren Ende mehr als `zeitfenster_jahre` (typisch
  // 10 Jahre) vor dem Stichtag liegt. Ausnahme: Wenn im Zeitfenster
  // mindestens `max_jahre` (typisch 5 Jahre) anrechenbare Zeit vorhanden
  // ist, greift die Reduktion NICHT.
  //
  // Im Gegensatz zu allen anderen Cap-Bypass-Sonderregeln WIRKT SICH DIESE
  // REGEL AUF DIE MATH-ENGINE AUS (Anrechnungsfaktor pro Item wird
  // deterministisch reduziert) — kein User-Toggle pro Zeile nötig, der
  // Sachbearbeiter markiert facheinschlägig sein/nicht sein bereits durch
  // die Anrechenbar-Checkbox.
  {
    typ: "facheinschlaegig_altersfaktor_regel",
    group: "altersfaktor",
    title: "Altersfaktor · Zeitwertige Anrechnung",
    description:
      "Halbiert den Anrechnungsfaktor (typisch faktor=0.5) für anrechenbare Vordienstzeiten, die vor dem Zeitfenster (typisch zeitfenster_jahre=10) enden. Ausnahme: Bei mindestens max_jahre (typisch 5) anrechenbaren Jahren im Zeitfenster greift die Reduktion NICHT — Person hat aktuelle Berufserfahrung.",
    defaults: { max_lueckendauer_monate: 0, faktor: 0.5, max_jahre: 5, zeitfenster_jahre: 10 },
    fields: ["zeitfenster_jahre", "max_jahre", "faktor"],
  },
];

// Iter 20.12 — Gruppen-Metadaten für UI-Overskrifter im „Neu hinzufügen"-Bereich.
const CAP_BYPASS_GROUPS = [
  { id: "selber_ag", title: "Selber-Arbeitgeber" },
  { id: "zeitfenster", title: "Zeitfenster" },
  // Iter 20.36.f2 — Wirtschaftstreuhand / Steuerberater-KV-Sonderregeln.
  { id: "wt", title: "Wirtschaftstreuhand" },
  // Iter 20.36.f3 — Altersfaktor: zeitwertige Anrechnung alter Vordienstzeiten.
  { id: "altersfaktor", title: "Altersfaktor" },
];

function emptyCapBypass(typ) {
  const tpl = CAP_BYPASS_TYPES.find((t) => t.typ === typ) || CAP_BYPASS_TYPES[0];
  return {
    typ: tpl.typ,
    max_lueckendauer_monate: tpl.defaults.max_lueckendauer_monate,
    faktor: tpl.defaults.faktor,
    max_jahre: tpl.defaults.max_jahre,
    zeitfenster_jahre: tpl.defaults.zeitfenster_jahre || 0,
    label_de: tpl.description,
    aktiv: true,
  };
}

export default function AdminKvStructuredEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kv, setKv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("stammdaten");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/kvs/${id}`);
        setKv(normalizeKv(data));
      } catch (err) {
        setError(extractError(err, "KV konnte nicht geladen werden."));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleSave = async () => {
    if (!kv) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: kv.name,
        kuerzel: kv.kuerzel,
        short_description: kv.short_description,
        ruleset: kv.ruleset,
        vordienst_anrechnungsregeln: kv.vordienst_anrechnungsregeln,
        cap_months: Number(kv.cap_months) || 0,
        llm_extra_context: kv.llm_extra_context || "",
        anrechnungs_faktoren: (kv.anrechnungs_faktoren || []).map((f) => ({
          ...f,
          faktor: Number(f.faktor),
          prioritaet: Number(f.prioritaet) || 10,
        })),
        sonderregeln: kv.sonderregeln || [],
        // Iter 19.66.1 — Cap-Bypass-Sonderregeln mit speichern.
        cap_bypass_sonderregeln: kv.cap_bypass_sonderregeln || [],
        ausbildungsregeln: (kv.ausbildungsregeln || []).map((a) => ({
          ...a,
          months: Number(a.months) || 0,
        })),
      };
      // Iter 19.66.1 — Backend exposes PATCH /api/admin/kvs/{id} (NOT PUT).
      // Pre-existing bug: api.put always returned HTTP 405 silently — every
      // save in the structured editor was a no-op. Fix via PATCH.
      const { data } = await api.patch(`/admin/kvs/${id}`, payload);
      setKv(normalizeKv(data));
      toast.success("KV-Regeln gespeichert. LLM-Markdown wurde neu generiert.");
    } catch (err) {
      setError(extractError(err, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-warm-500" data-testid="kv-edit-loading">
        Lade KV-Daten…
      </div>
    );
  }

  if (!kv) {
    return (
      <div className="p-8 text-center text-status-fehler-text" data-testid="kv-edit-error">
        {error || "KV nicht gefunden."}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-6" data-testid="kv-structured-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button
            onClick={() => navigate("/admin/kvs")}
            className="inline-flex items-center gap-1.5 text-sm text-warm-600 hover:text-ink-900 mb-2"
            data-testid="kv-edit-back"
          >
            <ArrowLeft size={14} weight="bold" /> Zurück zur Liste
          </button>
          <h1 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium">
            {kv.name || "—"}
          </h1>
          <div className="text-sm text-warm-600">
            {kv.kuerzel || "(kein Kürzel)"} · Gültig ab{" "}
            {kv.gueltig_von || "—"}
            {kv.gueltig_bis ? ` bis ${kv.gueltig_bis}` : " · bis auf weiteres"}
          </div>
        </div>
        <Button
          variant="brand"
          size="lg"
          onClick={handleSave}
          disabled={saving}
          data-testid="kv-edit-save"
        >
          <FloppyDisk size={16} weight="bold" />
          {saving ? "Speichert…" : "Speichern"}
        </Button>
      </div>

      {error && (
        <div className="mb-4 border border-status-fehler-border bg-status-fehler-bg text-status-fehler-text px-3 py-2 rounded-sm text-sm" data-testid="kv-edit-error-banner">
          {error}
        </div>
      )}

      {/* Tab navigation */}
      <div className="border-b border-warm-200 flex gap-1" data-testid="kv-edit-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={t.testId}
            className={[
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-crisp",
              tab === t.id
                ? "border-brand-500 text-brand-600"
                : "border-transparent text-warm-600 hover:text-ink-900",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="py-6">
        {tab === "stammdaten" && <StammdatenTab kv={kv} setKv={setKv} />}
        {tab === "faktoren" && <FaktorenTab kv={kv} setKv={setKv} />}
        {tab === "ausbildung" && <AusbildungTab kv={kv} setKv={setKv} />}
        {tab === "sonderregeln" && <SonderregelnTab kv={kv} setKv={setKv} />}
        {tab === "capbypass" && <CapBypassTab kv={kv} setKv={setKv} />}
        {tab === "markdown" && <MarkdownPreviewTab kv={kv} />}
      </div>
    </div>
  );
}

function normalizeKv(kv) {
  return {
    ...kv,
    anrechnungs_faktoren: kv.anrechnungs_faktoren || [],
    sonderregeln: kv.sonderregeln || [],
    cap_bypass_sonderregeln: kv.cap_bypass_sonderregeln || [],
    ausbildungsregeln: (kv.ausbildungsregeln || []).map((a) => ({
      ...EMPTY_AUSBILDUNG,
      ...a,
      synonyms: a.synonyms || [],
    })),
    cap_months: Number(kv.cap_months) || 0,
    llm_extra_context: kv.llm_extra_context || "",
  };
}

// ─── Tab 1: Stammdaten ─────────────────────────────────────────────────────
function StammdatenTab({ kv, setKv }) {
  const set = (key) => (e) => setKv({ ...kv, [key]: e.target.value });
  const setNum = (key) => (e) => setKv({ ...kv, [key]: Number(e.target.value) || 0 });

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
          Name <span className="text-status-fehler-text">*</span>
        </label>
        <Input value={kv.name || ""} onChange={set("name")} data-testid="kv-stammdaten-name" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
          Kürzel
        </label>
        <Input value={kv.kuerzel || ""} onChange={set("kuerzel")} data-testid="kv-stammdaten-kuerzel" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
          Kurzbeschreibung <span className="text-status-fehler-text">*</span>
        </label>
        <textarea
          value={kv.short_description || ""}
          onChange={set("short_description")}
          rows={3}
          className="w-full border border-warm-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
          data-testid="kv-stammdaten-description"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
            Höchstanrechnungsdauer (Monate)
          </label>
          <Input
            type="number"
            value={kv.cap_months || 0}
            onChange={setNum("cap_months")}
            min={0}
            max={240}
            data-testid="kv-stammdaten-cap"
          />
          <div className="text-[11px] text-warm-500 mt-1">
            {kv.cap_months > 0
              ? `= ${Math.floor(kv.cap_months / 12)} J. ${kv.cap_months % 12} M.`
              : "Keine Höchstgrenze"}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
            Gültig ab
          </label>
          <div className="text-sm text-ink-700 py-2">
            {kv.gueltig_von || "—"}{" "}
            <span className="text-warm-500 text-xs">
              (wird in Phase B bearbeitbar)
            </span>
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
          Zusätzliche LLM-Hinweise (Freitext, optional)
        </label>
        <textarea
          value={kv.llm_extra_context || ""}
          onChange={set("llm_extra_context")}
          rows={4}
          placeholder="Hinweise für die KI, die nicht ins strukturierte Schema passen…"
          className="w-full border border-warm-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-400"
          data-testid="kv-stammdaten-llm-extra"
        />
      </div>
    </div>
  );
}

// ─── Tab 2: Faktoren ────────────────────────────────────────────────────────
function FaktorenTab({ kv, setKv }) {
  const rules = kv.anrechnungs_faktoren || [];

  const update = (idx, patch) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setKv({ ...kv, anrechnungs_faktoren: next });
  };
  const remove = (idx) => {
    setKv({ ...kv, anrechnungs_faktoren: rules.filter((_, i) => i !== idx) });
  };
  const add = () => {
    setKv({ ...kv, anrechnungs_faktoren: [...rules, { ...EMPTY_FAKTOR }] });
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-warm-700">
        Pro Tätigkeitsart wird ein <strong>Faktor</strong> (0.0 – 1.0)
        festgelegt. Die KI nutzt diese Regeln deterministisch beim
        Klassifizieren — höhere Priorität gewinnt bei Konflikten.
      </p>

      {rules.length === 0 && (
        <div className="text-center py-8 text-warm-500 text-sm" data-testid="kv-faktoren-empty">
          Noch keine Faktor-Regeln definiert. Standardannahme: Faktor 1.0 = voll anrechenbar.
        </div>
      )}

      <div className="space-y-3">
        {rules.map((r, idx) => (
          <div
            key={idx}
            className="border border-warm-200 rounded-sm p-3 bg-warm-50/40"
            data-testid={`kv-faktor-row-${idx}`}
          >
            <div className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-5">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Trigger-Pattern (Regex)
                </label>
                <Input
                  value={r.trigger_pattern || ""}
                  onChange={(e) => update(idx, { trigger_pattern: e.target.value })}
                  placeholder='z. B. "Arbeiter(in)?"'
                  className="font-mono text-sm"
                  data-testid={`kv-faktor-${idx}-pattern`}
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Faktor
                </label>
                <select
                  value={r.faktor}
                  onChange={(e) => update(idx, { faktor: Number(e.target.value) })}
                  className="w-full border border-warm-300 rounded-sm px-2 py-2 text-sm bg-white"
                  data-testid={`kv-faktor-${idx}-value`}
                >
                  <option value={1.0}>1.0 (voll)</option>
                  <option value={0.5}>0.5 (hälftig)</option>
                  <option value={0.25}>0.25 (viertel)</option>
                  <option value={0.0}>0.0 (nicht anrechenbar)</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Priorität
                </label>
                <Input
                  type="number"
                  value={r.prioritaet || 10}
                  onChange={(e) => update(idx, { prioritaet: Number(e.target.value) })}
                  min={0}
                  max={100}
                  data-testid={`kv-faktor-${idx}-priority`}
                />
              </div>
              <div className="col-span-2 pt-5">
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-700">
                  <input
                    type="checkbox"
                    checked={Boolean(r.aktiv)}
                    onChange={(e) => update(idx, { aktiv: e.target.checked })}
                    data-testid={`kv-faktor-${idx}-aktiv`}
                  />
                  aktiv
                </label>
              </div>
              <div className="col-span-1 pt-4 text-right">
                <button
                  onClick={() => remove(idx)}
                  className="text-status-fehler-text hover:bg-status-fehler-bg p-1 rounded"
                  title="Regel löschen"
                  data-testid={`kv-faktor-${idx}-remove`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
              <div className="col-span-12">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Begründungs-Vorlage
                </label>
                <Input
                  value={r.begruendung_template || ""}
                  onChange={(e) => update(idx, { begruendung_template: e.target.value })}
                  placeholder="Wird in der Klassifizierungs-Begründung verwendet"
                  data-testid={`kv-faktor-${idx}-begruendung`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={add} data-testid="kv-faktor-add">
        <Plus size={14} weight="bold" /> Faktor-Regel hinzufügen
      </Button>
    </div>
  );
}

// ─── Tab 3: Ausbildungs-Boni ────────────────────────────────────────────────
function AusbildungTab({ kv, setKv }) {
  const rules = kv.ausbildungsregeln || [];

  const update = (idx, patch) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setKv({ ...kv, ausbildungsregeln: next });
  };
  const remove = (idx) => {
    setKv({ ...kv, ausbildungsregeln: rules.filter((_, i) => i !== idx) });
  };
  const add = () => {
    setKv({ ...kv, ausbildungsregeln: [...rules, { ...EMPTY_AUSBILDUNG }] });
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-warm-700">
        Fiktive Bonuszeiten für absolvierte Ausbildungen
        (z. B. HAK-Matura → +24 Monate). Diese erscheinen im{" "}
        <strong>Schritt 3 Dropdown</strong> und werden bei passenden
        Lebenslauf-Stichwörtern automatisch erkannt.
      </p>

      <div className="space-y-3">
        {rules.map((r, idx) => (
          <div
            key={idx}
            className="border border-warm-200 rounded-sm p-3 bg-warm-50/40"
            data-testid={`kv-ausbildung-row-${idx}`}
          >
            <div className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-5">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Label (Anzeigename)
                </label>
                <Input
                  value={r.label || ""}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  placeholder="z. B. HTL-Matura"
                  data-testid={`kv-ausbildung-${idx}-label`}
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Monate
                </label>
                <Input
                  type="number"
                  value={r.months || 0}
                  onChange={(e) => update(idx, { months: Number(e.target.value) })}
                  min={0}
                  max={240}
                  data-testid={`kv-ausbildung-${idx}-months`}
                />
              </div>
              <div className="col-span-2 pt-5">
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-700">
                  <input
                    type="checkbox"
                    checked={Boolean(r.bypass_cap)}
                    onChange={(e) => update(idx, { bypass_cap: e.target.checked })}
                    data-testid={`kv-ausbildung-${idx}-bypass`}
                  />
                  Über Höchstgrenze
                </label>
              </div>
              <div className="col-span-2 pt-5">
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-700">
                  <input
                    type="checkbox"
                    checked={Boolean(r.default_anrechenbar)}
                    onChange={(e) => update(idx, { default_anrechenbar: e.target.checked })}
                    data-testid={`kv-ausbildung-${idx}-default`}
                  />
                  default aktiv
                </label>
              </div>
              <div className="col-span-1 pt-4 text-right">
                <button
                  onClick={() => remove(idx)}
                  className="text-status-fehler-text hover:bg-status-fehler-bg p-1 rounded"
                  data-testid={`kv-ausbildung-${idx}-remove`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </div>
              <div className="col-span-12">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Synonyme (Komma-getrennt, für Lebenslauf-Auto-Match)
                </label>
                <Input
                  value={(r.synonyms || []).join(", ")}
                  onChange={(e) =>
                    update(idx, {
                      synonyms: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="z. B. HTL, Höhere Technische Lehranstalt, Matura HTL"
                  data-testid={`kv-ausbildung-${idx}-synonyms`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={add} data-testid="kv-ausbildung-add">
        <Plus size={14} weight="bold" /> Ausbildungs-Bonus hinzufügen
      </Button>
    </div>
  );
}

// ─── Tab 4: Sonderregeln ────────────────────────────────────────────────────
function SonderregelnTab({ kv, setKv }) {
  const rules = kv.sonderregeln || [];

  const update = (idx, patch) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setKv({ ...kv, sonderregeln: next });
  };
  const remove = (idx) => {
    setKv({ ...kv, sonderregeln: rules.filter((_, i) => i !== idx) });
  };
  const add = () => {
    setKv({ ...kv, sonderregeln: [...rules, { ...EMPTY_SONDERREGEL }] });
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <p className="text-sm text-warm-700">
        KV-spezifische Sonderregeln (1–3 pro KV typisch). Trigger + Effekt
        werden strukturiert eingegeben — die Engine prüft jede Zeile bei
        der Klassifizierung. Beispiele: <em>Kassakraft +1 J. über Höchstgrenze</em>,{" "}
        <em>Karenz vor 2019 nicht anrechenbar</em>.
      </p>

      <div className="space-y-3">
        {rules.map((r, idx) => (
          <SonderregelRow
            key={idx}
            rule={r}
            onChange={(patch) => update(idx, patch)}
            onRemove={() => remove(idx)}
            idx={idx}
          />
        ))}
      </div>

      <Button variant="outline" onClick={add} data-testid="kv-sonderregel-add">
        <Plus size={14} weight="bold" /> Sonderregel hinzufügen
      </Button>
    </div>
  );
}

function SonderregelRow({ rule, onChange, onRemove, idx }) {
  const trig = rule.trigger_condition || {};
  const eff = rule.effekt || {};
  const setTrig = (key, value) =>
    onChange({ trigger_condition: { ...trig, ...(value === "" || value == null ? { [key]: undefined } : { [key]: value }) } });
  const setEff = (key, value) =>
    onChange({ effekt: { ...eff, ...(value === "" || value == null ? { [key]: undefined } : { [key]: value }) } });

  return (
    <div className="border border-warm-200 rounded-sm p-3 bg-warm-50/40" data-testid={`kv-sonderregel-row-${idx}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <Input
          value={rule.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Regelname (z. B. Karenz vor 2019 nicht anrechenbar)"
          className="font-medium"
          data-testid={`kv-sonderregel-${idx}-name`}
        />
        <label className="inline-flex items-center gap-1.5 text-xs text-ink-700 shrink-0 pt-2">
          <input
            type="checkbox"
            checked={Boolean(rule.aktiv)}
            onChange={(e) => onChange({ aktiv: e.target.checked })}
            data-testid={`kv-sonderregel-${idx}-aktiv`}
          />
          aktiv
        </label>
        <button
          onClick={onRemove}
          className="text-status-fehler-text hover:bg-status-fehler-bg p-2 rounded shrink-0"
          data-testid={`kv-sonderregel-${idx}-remove`}
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Trigger-Bereich */}
        <div className="bg-white border border-warm-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-600 mb-2">
            WENN (Trigger)
          </div>
          <div className="space-y-2">
            <SelectField
              label="Kategorie"
              value={trig.kategorie || ""}
              onChange={(v) => setTrig("kategorie", v)}
              options={[
                { value: "", label: "(beliebig)" },
                { value: "vordienst", label: "Vordienst-Zeile" },
                { value: "ausbildung", label: "Ausbildungs-Bonus" },
              ]}
              testId={`kv-sonderregel-${idx}-kategorie`}
            />
            <InlineField
              label="Tätigkeit Regex"
              value={trig.taetigkeit_regex || ""}
              onChange={(v) => setTrig("taetigkeit_regex", v)}
              placeholder="z. B. Karenz"
              testId={`kv-sonderregel-${idx}-rx`}
            />
            <InlineField
              label="Label (exakt)"
              value={trig.label || ""}
              onChange={(v) => setTrig("label", v)}
              placeholder="z. B. Kassakraft (für Ausbildungs-Bonus)"
              testId={`kv-sonderregel-${idx}-trig-label`}
            />
            <InlineField
              label="Datum-Von nach"
              type="date"
              value={trig.datum_von_nach || ""}
              onChange={(v) => setTrig("datum_von_nach", v)}
              testId={`kv-sonderregel-${idx}-von-nach`}
            />
            <InlineField
              label="Datum-Bis vor"
              type="date"
              value={trig.datum_bis_vor || ""}
              onChange={(v) => setTrig("datum_bis_vor", v)}
              testId={`kv-sonderregel-${idx}-bis-vor`}
            />
          </div>
        </div>

        {/* Effekt-Bereich */}
        <div className="bg-white border border-warm-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-600 mb-2">
            DANN (Effekt)
          </div>
          <div className="space-y-2">
            <SelectField
              label="Faktor erzwingen"
              value={eff.faktor != null ? String(eff.faktor) : ""}
              onChange={(v) => setEff("faktor", v === "" ? undefined : Number(v))}
              options={[
                { value: "", label: "(nicht setzen)" },
                { value: "1", label: "1.0 (voll)" },
                { value: "0.5", label: "0.5 (hälftig)" },
                { value: "0.25", label: "0.25 (viertel)" },
                { value: "0", label: "0.0 (nicht anrechenbar)" },
              ]}
              testId={`kv-sonderregel-${idx}-eff-faktor`}
            />
            <CheckboxField
              label="Über Höchstgrenze (zählt ON TOP)"
              checked={Boolean(eff.cap_bypass)}
              onChange={(v) => setEff("cap_bypass", v ? true : undefined)}
              testId={`kv-sonderregel-${idx}-eff-bypass`}
            />
            <CheckboxField
              label="Nicht anrechenbar (= Faktor 0)"
              checked={Boolean(eff.nicht_anrechenbar)}
              onChange={(v) => setEff("nicht_anrechenbar", v ? true : undefined)}
              testId={`kv-sonderregel-${idx}-eff-nicht`}
            />
            <InlineField
              label="Monate-Sonderwert"
              type="number"
              value={eff.months_override ?? ""}
              onChange={(v) => setEff("months_override", v === "" ? undefined : Number(v))}
              placeholder="z. B. 36 (für Ausbildungs-Sonderfall)"
              testId={`kv-sonderregel-${idx}-eff-months`}
            />
            <InlineField
              label="Begründungs-Suffix"
              value={eff.begruendung_suffix || ""}
              onChange={(v) => setEff("begruendung_suffix", v)}
              placeholder="Wird an die Begründung angehängt"
              testId={`kv-sonderregel-${idx}-eff-suffix`}
            />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
          Begründung (für Audit / Doku)
        </label>
        <Input
          value={rule.begruendung || ""}
          onChange={(e) => onChange({ begruendung: e.target.value })}
          placeholder="z. B. § XY des KV Handel"
          data-testid={`kv-sonderregel-${idx}-begruendung`}
        />
      </div>
    </div>
  );
}

function InlineField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 w-32 shrink-0">
        {label}
      </div>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="text-sm flex-1"
        data-testid={testId}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 w-32 shrink-0">
        {label}
      </div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 border border-warm-300 rounded-sm px-2 py-1.5 text-sm bg-white"
        data-testid={testId}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckboxField({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      {label}
    </label>
  );
}

// ─── Tab 5: Sonderregeln (Iter 19.66.1 + Iter 20.12) ─────────────────────────
// KV-spezifische Anrechnungs-Ausnahmen. Aktuell zwei Regel-Klassen:
//   • Selber-Arbeitgeber (Cap-Bypass in 3 Varianten)
//   • Zeitfenster (nur letzte N Jahre relevant)
// Weitere Klassen können additiv ergänzt werden. Frontend zeigt pro Eintrag
// NUR die für den jeweiligen Typ relevanten Felder.
function CapBypassTab({ kv, setKv }) {
  const rules = kv.cap_bypass_sonderregeln || [];

  const updateRule = (idx, patch) => {
    const next = rules.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setKv({ ...kv, cap_bypass_sonderregeln: next });
  };
  const removeRule = (idx) => {
    setKv({
      ...kv,
      cap_bypass_sonderregeln: rules.filter((_, i) => i !== idx),
    });
  };
  const addRule = (typ) => {
    setKv({
      ...kv,
      cap_bypass_sonderregeln: [...rules, emptyCapBypass(typ)],
    });
  };

  return (
    <div className="max-w-4xl space-y-5" data-testid="kv-edit-capbypass-panel">
      <div className="border border-warm-200 rounded-sm bg-warm-50 px-4 py-3 text-sm text-warm-700">
        <p className="font-semibold text-ink-900 mb-1">
          Was sind Sonderregeln?
        </p>
        <p className="leading-relaxed">
          Hier definierst du KV-spezifische Ausnahmen und Sonderfälle bei der
          Anrechnung von Vordienstzeiten — z.&nbsp;B. bevorzugte Behandlung
          beim selben Arbeitgeber oder ein zeitliches Relevanzfenster. Jede
          Regel wirkt zusätzlich zu den Standard-Anrechnungsregeln dieses KV.
        </p>
      </div>

      {rules.length === 0 && (
        <div
          className="text-sm text-warm-600 italic"
          data-testid="kv-edit-capbypass-empty"
        >
          Für diesen KV sind keine Sonderregeln aktiv — es gelten die
          Standard-Anrechnungsregeln (voller Faktor bis zur Höchstanrechnungsdauer).
        </div>
      )}

      {rules.map((rule, idx) => {
        const meta = CAP_BYPASS_TYPES.find((t) => t.typ === rule.typ) || CAP_BYPASS_TYPES[0];
        return (
          <div
            key={idx}
            className="border border-warm-200 bg-white rounded-sm p-4 space-y-3"
            data-testid={`kv-edit-capbypass-row-${idx}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-widest font-semibold text-brand-600">
                    {meta.title}
                  </span>
                  {!rule.aktiv && (
                    <span className="text-xs text-warm-500 italic">(inaktiv)</span>
                  )}
                </div>
                <p className="text-xs text-warm-600 leading-relaxed">
                  {meta.description}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeRule(idx)}
                data-testid={`kv-edit-capbypass-remove-${idx}`}
                title="Sonderregel entfernen"
              >
                <Trash size={14} weight="bold" />
              </Button>
            </div>

            {meta.fields.includes("max_lueckendauer_monate") && (
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Maximale Lückendauer (Monate)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={240}
                  value={rule.max_lueckendauer_monate || 0}
                  onChange={(e) =>
                    updateRule(idx, {
                      max_lueckendauer_monate: Number(e.target.value) || 0,
                    })
                  }
                  data-testid={`kv-edit-capbypass-lueckendauer-${idx}`}
                  className="max-w-[140px]"
                />
              </div>
            )}

            {meta.fields.includes("faktor") && (
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Anrechnungsfaktor (0.0 – 1.0)
                </label>
                <Input
                  type="number"
                  step="0.05"
                  min={0}
                  max={1}
                  value={rule.faktor ?? 0.5}
                  onChange={(e) =>
                    updateRule(idx, { faktor: Number(e.target.value) || 0 })
                  }
                  data-testid={`kv-edit-capbypass-faktor-${idx}`}
                  className="max-w-[140px]"
                />
              </div>
            )}

            {meta.fields.includes("max_jahre") && (
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Maximale Anrechnungsdauer (Jahre, 0 = unbegrenzt)
                </label>
                <Input
                  type="number"
                  min={0}
                  max={50}
                  value={rule.max_jahre || 0}
                  onChange={(e) =>
                    updateRule(idx, { max_jahre: Number(e.target.value) || 0 })
                  }
                  data-testid={`kv-edit-capbypass-maxjahre-${idx}`}
                  className="max-w-[140px]"
                />
              </div>
            )}

            {meta.fields.includes("zeitfenster_jahre") && (
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                  Zeitfenster (Jahre vor Stichtag)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={rule.zeitfenster_jahre || 0}
                  onChange={(e) =>
                    updateRule(idx, { zeitfenster_jahre: Number(e.target.value) || 0 })
                  }
                  data-testid={`kv-edit-capbypass-zeitfenster-${idx}`}
                  className="max-w-[140px]"
                />
                <p className="text-[11px] text-warm-500 mt-1 leading-relaxed">
                  Vordienstzeiten, die vollständig vor dem Stichtag − N Jahre
                  liegen, werden ausgeschlossen. Teilweise ins Fenster reichende
                  Zeiträume werden am Fenster-Anfang abgeschnitten.
                </p>
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest font-semibold text-warm-500 mb-1 block">
                Erklärungstext (für UI/Tooltip)
              </label>
              <textarea
                value={rule.label_de || ""}
                onChange={(e) =>
                  updateRule(idx, { label_de: e.target.value })
                }
                rows={2}
                className="w-full border border-warm-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                data-testid={`kv-edit-capbypass-label-${idx}`}
              />
            </div>

            <CheckboxField
              checked={rule.aktiv}
              onChange={(v) => updateRule(idx, { aktiv: v })}
              label="Regel aktiv"
              testId={`kv-edit-capbypass-active-${idx}`}
            />
          </div>
        );
      })}

      <div className="border-t border-warm-200 pt-4 space-y-4">
        <div className="text-xs uppercase tracking-widest font-semibold text-warm-500">
          Neue Sonderregel hinzufügen
        </div>
        {CAP_BYPASS_GROUPS.map((grp) => {
          const groupTypes = CAP_BYPASS_TYPES.filter((t) => t.group === grp.id);
          if (groupTypes.length === 0) return null;
          return (
            <div key={grp.id} data-testid={`kv-edit-capbypass-group-${grp.id}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500/80 mb-2">
                {grp.title}
              </div>
              <div className="flex flex-wrap gap-2">
                {groupTypes.map((t) => {
                  const alreadyHas = rules.some((r) => r.typ === t.typ);
                  return (
                    <Button
                      key={t.typ}
                      variant="outline"
                      size="sm"
                      onClick={() => addRule(t.typ)}
                      disabled={alreadyHas}
                      data-testid={`kv-edit-capbypass-add-${t.typ}`}
                      title={alreadyHas ? "Bereits hinzugefügt" : t.description}
                    >
                      + {t.title}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tab 6: LLM-Markdown Preview ─────────────────────────────────────────────
function MarkdownPreviewTab({ kv }) {
  const md = kv.llm_markdown_cache || "(Wird beim Speichern erzeugt.)";
  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 mb-3 text-sm text-warm-700">
        <Eye size={14} weight="bold" />
        <span>
          Dies ist der <strong>autogenerierte Markdown-Text</strong>, den die
          KI beim Klassifizieren als Regelwerk-Input erhält. Wird beim
          Speichern aus den strukturierten Tabs neu erzeugt.
        </span>
      </div>
      <pre
        className="border border-warm-200 rounded-sm bg-warm-50/40 p-4 text-[12px] font-mono leading-snug whitespace-pre-wrap text-ink-800 max-h-[600px] overflow-y-auto"
        data-testid="kv-edit-markdown-preview"
      >
        {md}
      </pre>
    </div>
  );
}
